import type { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import {
  addToCart,
  changePassword,
  getSelection,
  loginUser,
  logoutUser,
  resetPassword,
  resetPasswordEmail,
  setCartItemQuantity,
  setPromo,
  signUpUser,
  subscribeNewsletter,
  updateUser,
  type AddToCartOptions,
} from "./fetchers";
import type { LoginVariables } from "./fetchers/loginUser";
import type { User } from "./fetchers/updateUser";
import defaultFormatter, {
  type DefaultSelection,
} from "./formatters/formatSelection";

import {
  addCustomGiftCertificate,
  addGiftCertificate,
  type CustomGiftVariables,
} from "./fetchers/giftcard/addGiftCertificate";
import type { PromoVariables } from "./fetchers/setPromo";
import type { NewsletterVariables } from "./fetchers/subscribeNewsletter";
import updateCountry, { type CountryVariables } from "./fetchers/updateCountry";

/**
 * Selection fetchers for the Centra API
 * Uses a Class to allow for easier typing of the formatter
 * All functions use arrow syntax with lexical this to avoid this binding issues.
 */
class SelectionFetchers<FormattedSelection> {
  constructor(
    public readonly formatter: (
      arg0: BaseSelectionResponse
    ) => FormattedSelection,
    private readonly apiUrl: string
  ) {}

  addToCart = (
    variables: AddToCartOptions<FormattedSelection>["variables"],
    customerToken: string
  ): Promise<FormattedSelection> => {
    return addToCart<FormattedSelection>({
      variables,
      customerToken,
      apiUrl: this.apiUrl,
      formatter: this.formatter,
    });
  };

  changePassword = (
    variables: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    },
    customerToken: string
  ): Promise<FormattedSelection> => {
    return changePassword<FormattedSelection>({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  getSelection = ({
    setToken,
    token,
    signal,
  }: {
    token?: string | null;
    setToken: (token: string) => void;
    signal?: AbortSignal;
  }) => {
    return getSelection({
      token,
      setToken,
      api: this.apiUrl,
      formatter: this.formatter,
      signal,
    });
  };

  loginUser = (variables: LoginVariables, customerToken: string) => {
    return loginUser<FormattedSelection>({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  logoutUser = (customerToken: string) => {
    return logoutUser({
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  resetPassword = (variables: { newPassword: string; i: any; id: any }) => {
    return resetPassword({
      variables,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  resetPasswordEmail = (variables: { email: string; linkUri: string }) => {
    return resetPasswordEmail({
      variables,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  setCartItemQuantity = (
    variables: { line: string; newQuantity: number },
    customerToken: string
  ) => {
    return setCartItemQuantity({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  vouchers = (variables: PromoVariables, customerToken: string) => {
    return setPromo({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  signUpUser = (
    variables: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      newsletter?: boolean;
    },
    customerToken: string
  ) => {
    return signUpUser({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  subscribeNewsletter = (
    variables: NewsletterVariables,
    customerToken: string
  ) => {
    return subscribeNewsletter({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  updateUser = (variables: User, customerToken: string) => {
    return updateUser({
      variables,
      customerToken,
      api: this.apiUrl,
      formatter: this.formatter,
    });
  };

  addCustomGiftCertificate = (
    variables: CustomGiftVariables,
    giftToken: string
  ) => {
    return addCustomGiftCertificate({
      variables,
      giftToken,
      apiUrl: this.apiUrl,
      formatter: this.formatter,
    });
  };

  addGiftCertificate = (
    variables: { certificate: string },
    giftToken: string
  ) => {
    return addGiftCertificate({
      variables,
      giftToken,
      apiUrl: this.apiUrl,
      formatter: this.formatter,
    });
  };

  updateCountry = (
    variables: CountryVariables,
    token: string
  ) => {
    return updateCountry({
      variables,
      token,
      apiUrl: this.apiUrl,
      formatter: this.formatter,
    })
  }
}

/**
 * Selection fetchers factory for the Centra API
 * @return SelectionFetchers - A class with all the selection fetchers
 */
export function buildSelectionFetchers<
  FormattedSelection extends DefaultSelection,
>({ apiUrl }: { apiUrl: string }): SelectionFetchers<FormattedSelection>;

export function buildSelectionFetchers<FormattedSelection>({
  apiUrl,
  formatter,
}: {
  formatter: (arg0: BaseSelectionResponse) => FormattedSelection;
  apiUrl: string;
}): SelectionFetchers<FormattedSelection>;

export function buildSelectionFetchers<FormattedSelection>({
  formatter,
  apiUrl: api,
}: {
  formatter?: (
    arg0: BaseSelectionResponse
  ) => FormattedSelection | DefaultSelection;
  apiUrl: string;
}): SelectionFetchers<FormattedSelection | DefaultSelection> {
  const fetchers = new SelectionFetchers<FormattedSelection | DefaultSelection>(
    formatter || defaultFormatter,
    api
  );
  return fetchers;
}

export { type SelectionFetchers };
